import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bot,
  Send,
  Mic,
  MicOff,
  Plus,
  Sparkles,
  Trash2,
  MessageSquare,
  Loader2,
  ChevronRight,
  Globe,
  ExternalLink,
} from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import AIMarkdown from "@/components/shared/AIMarkdown";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";

export default function NexreqAI() {
  const navigate = useNavigate();
  const [briefing, setBriefing] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [actions, setActions] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [statusHint, setStatusHint] = useState("");
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const scrollRef = useRef(null);

  const RESEARCH_CHIPS = [
    { label: "Latest funding", template: "Look up the latest funding round for " },
    { label: "Recent news", template: "Give me recent news about " },
    { label: "Tech stack", template: "Look up online the tech stack of " },
    { label: "Leadership changes", template: "Search for recent leadership changes at " },
    { label: "Hiring signals", template: "Look up hiring signals for " },
    { label: "News", template: "Latest news about " },
  ];

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get("/nexreq-ai/sessions", { params: { limit: 50 } });
      setSessions(data.items || []);
    } catch {}
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [b, s] = await Promise.all([
          api.get("/nexreq-ai/briefing"),
          api.get("/nexreq-ai/suggestions"),
        ]);
        setBriefing(b.data);
        setSuggestions(s.data.items || []);
      } catch {
        setBriefing({ priorities: [] });
      }
    })();
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    if (scrollRef.current)
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const openSession = async (sid) => {
    try {
      const { data } = await api.get(`/nexreq-ai/sessions/${sid}`);
      setSessionId(sid);
      setMessages(
        (data.messages || []).map((m) => ({
          role: m.role,
          content: m.content,
          sources: m.sources || undefined,
        }))
      );
      setActions([]);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const newChat = () => {
    setSessionId(null);
    setMessages([]);
    setActions([]);
    setInput("");
  };

  const deleteSession = async (sid, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await api.delete(`/nexreq-ai/sessions/${sid}`);
      if (sid === sessionId) newChat();
      loadSessions();
    } catch (err) {
      toast.error(apiErr(err));
    }
  };

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    setStatusHint("");
    setMessages((rows) => [...rows, { role: "user", content: text }]);
    setStreaming(true);
    setMessages((rows) => [...rows, { role: "assistant", content: "" }]);
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/nexreq-ai/chat`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId, message: text }),
        }
      );
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let answer = "";
      let done = false;
      while (!done) {
        const chunk = await reader.read();
        done = chunk.done;
        if (!chunk.value) continue;
        for (const line of decoder.decode(chunk.value).split("\n")) {
          if (!line.startsWith("data: ")) continue;
          try {
            const ev = JSON.parse(line.slice(6));
            if (ev.type === "delta") {
              answer += ev.content;
              setMessages((rows) => [
                ...rows.slice(0, -1),
                { ...rows[rows.length - 1], role: "assistant", content: answer },
              ]);
            } else if (ev.type === "status") {
              setStatusHint(ev.content || "");
            } else if (ev.type === "sources") {
              setMessages((rows) => [
                ...rows.slice(0, -1),
                { ...rows[rows.length - 1], sources: ev.items || [] },
              ]);
            } else if (ev.type === "candidates") {
              setMessages((rows) => [
                ...rows,
                { role: "assistant", kind: "candidates", items: ev.items },
              ]);
            } else if (ev.type === "done") {
              setSessionId(ev.session_id);
              setActions(ev.actions || []);
            } else if (ev.type === "error") {
              toast.error(ev.message);
            }
          } catch {}
        }
      }
      loadSessions();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setStreaming(false);
      setStatusHint("");
    }
  };

  const toggleMic = () => {
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice input isn't supported in this browser");
      return;
    }
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = "en-US";
    let final = "";
    rec.onresult = (evt) => {
      let interim = "";
      for (let i = evt.resultIndex; i < evt.results.length; i++) {
        const t = evt.results[i][0].transcript;
        if (evt.results[i].isFinal) final += t;
        else interim += t;
      }
      setInput(final || interim);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recognitionRef.current = rec;
    rec.start();
    setListening(true);
  };

  return (
    <div className="grid gap-4 md:grid-cols-[260px_1fr_260px] h-[calc(100vh-140px)]">
      {/* Left: session sidebar */}
      <Card className="p-3 flex flex-col overflow-hidden">
        <Button
          onClick={newChat}
          className="mb-3"
          data-testid="ai-new-chat"
          variant="outline"
        >
          <Plus className="h-4 w-4 mr-1" /> New chat
        </Button>
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
          Recent
        </div>
        <div className="flex-1 overflow-y-auto space-y-1">
          {sessions.length === 0 ? (
            <div className="text-xs text-muted-foreground">
              Your conversations will appear here.
            </div>
          ) : (
            sessions.map((s) => (
              <div
                key={s.session_id}
                data-testid={`ai-session-${s.session_id}`}
                onClick={() => openSession(s.session_id)}
                className={`group text-sm px-2 py-1.5 rounded cursor-pointer hover:bg-accent flex items-center gap-2 ${
                  s.session_id === sessionId ? "bg-accent" : ""
                }`}
              >
                <MessageSquare className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <div className="truncate flex-1">{s.title}</div>
                <button
                  onClick={(e) => deleteSession(s.session_id, e)}
                  className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-600"
                  aria-label="delete"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))
          )}
        </div>
      </Card>

      {/* Center: chat pane */}
      <Card className="flex flex-col overflow-hidden">
        <div className="border-b p-3 flex items-center gap-2">
          <Bot className="h-5 w-5 text-[#255058]" />
          <div>
            <div className="font-semibold">NEXREQ AI</div>
            <div className="text-xs text-muted-foreground">
              Your personal work assistant · voice-ready
            </div>
          </div>
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && (
            <EmptyState
              greeting={briefing?.greeting}
              priorities={briefing?.priorities || []}
              suggestions={suggestions}
              onPick={(s) => send(s)}
            />
          )}
          {messages.map((m, i) => (
            <MessageBubble
              key={i}
              message={m}
              navigate={navigate}
              streaming={streaming && i === messages.length - 1 && m.role === "assistant"}
            />
          ))}
          {streaming && messages[messages.length - 1]?.content === "" && (
            <TypingDots hint={statusHint} />
          )}
        </div>
        {messages.length > 0 && (
          <div className="border-t px-3 py-2 flex flex-wrap gap-1.5 bg-muted/30" data-testid="ai-research-chips">
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground pr-1">
              <Globe className="h-3 w-3" /> Research
            </span>
            {RESEARCH_CHIPS.map((chip) => (
              <button
                key={chip.label}
                type="button"
                data-testid={`ai-research-chip-${chip.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setInput(chip.template)}
                className="text-xs px-2 py-1 rounded-full border bg-white hover:border-[#255058] hover:text-[#255058] transition"
              >
                {chip.label}
              </button>
            ))}
          </div>
        )}
        {actions.length > 0 && (
          <div className="border-t p-2 flex flex-wrap gap-2">
            {actions.map((a, i) => (
              <Button
                key={i}
                size="sm"
                variant="outline"
                onClick={() => navigate(a.path)}
                data-testid={`ai-action-${i}`}
              >
                {a.label} <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            ))}
          </div>
        )}
        <div className="border-t p-3 flex items-end gap-2">
          <Textarea
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Ask NEXREQ AI anything — Shift+Enter for a new line"
            className="resize-none min-h-[44px] max-h-[160px]"
            data-testid="ai-input"
          />
          <Button
            onClick={toggleMic}
            variant={listening ? "default" : "outline"}
            size="icon"
            data-testid="ai-mic"
            title={listening ? "Stop listening" : "Voice input"}
          >
            {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </Button>
          <Button
            onClick={() => send()}
            disabled={streaming || !input.trim()}
            className="bg-[#255058] hover:bg-[#1c3d43]"
            data-testid="ai-send"
          >
            {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </Card>

      {/* Right: daily briefing panel */}
      <Card className="p-3 overflow-y-auto">
        <div className="flex items-center gap-2 font-semibold text-sm mb-2">
          <Sparkles className="h-4 w-4 text-[#255058]" /> Today
        </div>
        {briefing?.greeting && (
          <div className="text-sm mb-3">{briefing.greeting}</div>
        )}
        {(briefing?.priorities || []).length > 0 ? (
          <div className="space-y-2">
            {briefing.priorities.map((p, i) => (
              <div key={i} className="text-xs border rounded px-2 py-1.5 leading-snug">
                {p}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">
            Nothing pressing right now. Ask NEXREQ AI to review your day.
          </div>
        )}
        {(briefing?.context?.recommended_actions || []).length > 0 && (
          <div className="mt-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
              Quick jump
            </div>
            <div className="space-y-1">
              {briefing.context.recommended_actions.map((a, i) => (
                <button
                  key={i}
                  onClick={() => navigate(a.path)}
                  className="w-full text-left text-xs px-2 py-1.5 rounded border hover:border-[#255058] hover:text-[#255058]"
                >
                  {a.label} →
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function EmptyState({ greeting, priorities, suggestions, onPick }) {
  return (
    <div className="max-w-2xl mx-auto py-6">
      <div className="text-2xl font-semibold mb-1">
        {greeting || "Hi. How can I help today?"}
      </div>
      <div className="text-sm text-muted-foreground mb-4">
        Ask about your work in plain English. I read from your Nexreq data with the same
        permissions you have — I won't invent anything.
      </div>
      {priorities.length > 0 && (
        <div className="mb-4 border-l-4 border-[#255058] pl-3">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
            On your plate right now
          </div>
          <ul className="text-sm space-y-0.5">
            {priorities.slice(0, 3).map((p, i) => (
              <li key={i}>• {p}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
        Suggested prompts
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {suggestions.slice(0, 8).map((s, i) => (
          <button
            key={i}
            onClick={() => onPick(s)}
            data-testid={`ai-suggestion-${i}`}
            className="text-left text-sm px-3 py-2 rounded border hover:border-[#255058] hover:bg-accent/40 transition"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ message, navigate, streaming }) {
  if (message.kind === "candidates") {
    return (
      <div className="mr-10 space-y-2">
        <div className="text-xs text-muted-foreground">Matched candidates</div>
        {(message.items || []).map((c) => (
          <div
            key={c.id}
            className="border rounded p-2 flex items-center justify-between"
          >
            <div>
              <div className="font-medium text-sm">{c.full_name}</div>
              <div className="text-xs text-muted-foreground">
                {c.current_title} · {c.current_employer} · {(c.skills || []).slice(0, 4).join(", ")}
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/candidates/${c.id}`)}
            >
              Open
            </Button>
          </div>
        ))}
      </div>
    );
  }
  const isUser = message.role === "user";
  const sources = message.sources || null;
  const citationMap = sources
    ? sources.reduce((acc, s, i) => {
        acc[String(i + 1)] = s.url;
        return acc;
      }, {})
    : null;
  return (
    <div className={isUser ? "ml-16 text-right" : "mr-16"}>
      <div
        className={`inline-block px-3 py-2 rounded-lg text-sm text-left ${
          isUser
            ? "bg-[#255058] text-white whitespace-pre-wrap"
            : "bg-muted text-foreground"
        }`}
      >
        {isUser ? (
          message.content
        ) : (
          <AIMarkdown
            text={message.content}
            citationMap={citationMap}
            testId={`ai-msg-${message.role}`}
          />
        )}
        {streaming && (
          <span className="inline-block ml-1 animate-pulse">▌</span>
        )}
      </div>
      {sources && sources.length > 0 && <SourceList sources={sources} />}
    </div>
  );
}

// GroundedText is now handled inside AIMarkdown via citationMap; keep as
// a thin fallback so any legacy imports don't break.
function GroundedText({ text, sources }) {
  const citationMap = (sources || []).reduce((acc, s, i) => {
    acc[String(i + 1)] = s.url;
    return acc;
  }, {});
  return <AIMarkdown text={text} citationMap={citationMap} />;
}

function SourceList({ sources }) {
  return (
    <div className="mt-1.5 space-y-1" data-testid="ai-sources">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
        <Globe className="h-3 w-3" /> Sources
      </div>
      <ol className="space-y-1 text-left">
        {sources.map((s, i) => (
          <li key={i} className="text-xs">
            <a
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`ai-source-${i + 1}`}
              className="inline-flex items-start gap-1 text-[#255058] hover:underline"
            >
              <span className="font-semibold shrink-0">[{i + 1}]</span>
              <span className="truncate max-w-[380px]">{s.title}</span>
              <ExternalLink className="h-3 w-3 shrink-0 mt-[2px] opacity-60" />
            </a>
            {s.snippet && (
              <div className="text-[11px] text-muted-foreground pl-5 truncate max-w-[420px]">
                {s.snippet}
              </div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function TypingDots({ hint }) {
  return (
    <div className="mr-16">
      <div className="inline-flex items-center gap-1 bg-muted px-3 py-2 rounded-lg">
        {hint ? (
          <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5" data-testid="ai-status-hint">
            <Globe className="h-3 w-3 animate-pulse" /> {hint}
          </span>
        ) : (
          <>
            <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce [animation-delay:-0.3s]" />
            <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce [animation-delay:-0.15s]" />
            <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce" />
          </>
        )}
      </div>
    </div>
  );
}
